const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

/**
 * Normalizes page number and size to valid ranges
 * @param {number} [pageNumber=1]
 * @param {number} [pageSize=10]
 * @returns {{ page: number, size: number }}
 */
export const normalizePaging = (pageNumber, pageSize) => {
    const page = Math.max(1, pageNumber || 1);
    const size = Math.max(1, Math.min(pageSize || DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE));
    return { page, size };
};

/**
 * Applies sorting to an array of items
 * @template T
 * @param {T[]} items
 * @param {string} [sortKey]
 * @param {boolean} [descending=false]
 * @param {Object.<string, (item: T) => any>} sortMap
 * @param {string} defaultKey
 * @returns {T[]}
 */
export const applySort = (items, sortKey, descending = false, sortMap, defaultKey) => {
    if (!Array.isArray(items) || items.length === 0) {
        return items;
    }

    const key = sortKey && sortMap[sortKey] ? sortKey : defaultKey;
    const selector = sortMap[key];

    if (!selector) {
        return items;
    }

    const sorted = [...items].sort((a, b) => {
        const aVal = selector(a);
        const bVal = selector(b);

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
        }

        if (aVal < bVal) return -1;
        if (aVal > bVal) return 1;
        return 0;
    });

    return descending ? sorted.reverse() : sorted;
};

/**
 * Applies search filter across specified fields
 * @template T
 * @param {T[]} items
 * @param {string} [searchTerm]
 * @param {string|Object} [searchOption]
 * @param {Object.<string, (item: T) => string|string[]>} searchMap
 * @param {boolean} [searchAllWhenOptionMissing=true]
 * @param {string} [allOptionKey='all']
 * @returns {T[]}
 */
export const applySearch = (
    items,
    searchTerm,
    searchOption,
    searchMap,
    searchAllWhenOptionMissing = true,
    allOptionKey = 'all'
) => {
    if (!Array.isArray(items) || !searchTerm || !searchTerm.trim()) {
        return items;
    }

    if (!searchMap || Object.keys(searchMap).length === 0) {
        return items;
    }

    const term = searchTerm.trim().toLowerCase();

    const optionValue =
        typeof searchOption === 'object' && searchOption?.value !== undefined
            ? searchOption.value
            : searchOption;

    const normalizedOption =
        typeof optionValue === 'string' ? optionValue.toLowerCase() : optionValue;

    const isAllRequested =
        normalizedOption === allOptionKey.toLowerCase() ||
        normalizedOption === 0 ||
        (normalizedOption == null && searchAllWhenOptionMissing);

    const searchFields = isAllRequested
        ? Object.values(searchMap)
        : searchMap[normalizedOption] || searchMap[optionValue]
          ? [searchMap[normalizedOption] || searchMap[optionValue]]
          : [];

    if (searchFields.length === 0) {
        return items;
    }

    return items.filter((item) => {
        return searchFields.some((selector) => {
            const value = selector(item);

            if (Array.isArray(value)) {
                return value.some((v) => matchesSearchTerm(v, term));
            }

            return matchesSearchTerm(value, term);
        });
    });
};

const matchesSearchTerm = (value, term) => {
    if (value == null) return false;
    return value.toString().toLowerCase().includes(term);
};

/**
 * Applies advanced filters based on filter configuration
 * @template T
 * @param {T[]} items
 * @param {Object.<string, any|any[]>} filterValues
 * @param {Object.<string, (item: T, filterValue: any|any[]) => boolean>} filterMap
 * @returns {T[]}
 */
export const applyFilters = (items, filterValues, filterMap) => {
    if (!Array.isArray(items) || !filterValues || !filterMap) {
        return items;
    }

    return items.filter((item) => {
        return Object.entries(filterValues).every(([key, value]) => {
            if (value == null || (Array.isArray(value) && value.length === 0)) {
                return true;
            }

            const filterFn = filterMap[key];
            if (!filterFn) {
                return true;
            }

            return filterFn(item, value);
        });
    });
};

/**
 * Applies pagination to an array of items
 * @template T
 * @param {T[]} items
 * @param {number} page
 * @param {number} size
 * @returns {T[]}
 */
export const applyPaging = (items, page, size) => {
    if (!Array.isArray(items)) {
        return items;
    }

    const start = (page - 1) * size;
    return items.slice(start, start + size);
};

/**
 * Creates a paged result with metadata
 * @template T
 * @param {T[]} items
 * @param {number} page
 * @param {number} size
 * @returns {{ items: T[], total: number, page: number, size: number, totalPages: number }}
 */
export const toPagedResult = (items, page, size) => {
    if (!Array.isArray(items)) {
        return {
            items: [],
            total: 0,
            page,
            size,
            totalPages: 0,
        };
    }

    const total = items.length;
    const pagedItems = applyPaging(items, page, size);

    return {
        items: pagedItems,
        total,
        page,
        size,
        totalPages: Math.ceil(total / size),
    };
};

/**
 * Fluent query builder for chaining operations
 * @template T
 */
export class DataQuery {
    constructor(items) {
        this.items = Array.isArray(items) ? items : [];
    }

    /**
     * Apply sorting
     * @param {string} [sortKey]
     * @param {boolean} [descending=false]
     * @param {Object.<string, (item: T) => any>} sortMap
     * @param {string} defaultKey
     * @returns {DataQuery<T>}
     */
    sort(sortKey, descending, sortMap, defaultKey) {
        this.items = applySort(this.items, sortKey, descending, sortMap, defaultKey);
        return this;
    }

    /**
     * Apply search filter
     * @param {string} [searchTerm]
     * @param {string|Object} [searchOption]
     * @param {Object.<string, (item: T) => string|string[]>} searchMap
     * @param {boolean} [searchAllWhenOptionMissing=true]
     * @param {string} [allOptionKey='all']
     * @returns {DataQuery<T>}
     */
    search(searchTerm, searchOption, searchMap, searchAllWhenOptionMissing, allOptionKey) {
        this.items = applySearch(
            this.items,
            searchTerm,
            searchOption,
            searchMap,
            searchAllWhenOptionMissing,
            allOptionKey
        );
        return this;
    }

    /**
     * Apply advanced filters
     * @param {Object.<string, any|any[]>} filterValues
     * @param {Object.<string, (item: T, filterValue: any|any[]) => boolean>} filterMap
     * @returns {DataQuery<T>}
     */
    filter(filterValues, filterMap) {
        this.items = applyFilters(this.items, filterValues, filterMap);
        return this;
    }

    /**
     * Apply pagination
     * @param {number} page
     * @param {number} size
     * @returns {DataQuery<T>}
     */
    page(page, size) {
        this.items = applyPaging(this.items, page, size);
        return this;
    }

    /**
     * Execute and return results
     * @returns {T[]}
     */
    toArray() {
        return this.items;
    }

    /**
     * Execute and return paged results with metadata
     * @param {number} page
     * @param {number} size
     * @returns {{ items: T[], total: number, page: number, size: number, totalPages: number }}
     */
    toPagedResult(page, size) {
        return toPagedResult(this.items, page, size);
    }

    /**
     * Get count of items
     * @returns {number}
     */
    count() {
        return this.items.length;
    }
}

/**
 * Creates a new DataQuery instance
 * @template T
 * @param {T[]} items
 * @returns {DataQuery<T>}
 */
export const query = (items) => new DataQuery(items);
